// Revision Utilities
import { 
  getUserWrongQuestions, 
  getQuestionsFromBank, 
  savePracticeSession,
  saveUserRevisionData,
  getUserRevisionDataByUserId
} from '../firebase/collections';

// Generate revision test from wrong questions
export const generateRevisionTest = async (userId, options = {}) => {
  try {
    const {
      topic = null,
      questionCount = 20,
      includeSimilar = true,
      difficulty = 'mixed'
    } = options;

    // Get user's wrong questions
    const wrongQuestionsResult = await getUserWrongQuestions(userId, 100);
    if (!wrongQuestionsResult.success) {
      throw new Error('Failed to load wrong questions');
    }

    let testQuestions = wrongQuestionsResult.data;

    // Filter by topic if specified
    if (topic) {
      testQuestions = testQuestions.filter(q => 
        (q.topic === topic || q.subject === topic)
      );
    }

    // Filter by difficulty if specified
    if (difficulty !== 'mixed') {
      testQuestions = testQuestions.filter(q => 
        q.difficulty === difficulty
      );
    }

    // Limit questions
    testQuestions = testQuestions.slice(0, questionCount);

    // Add similar questions if requested
    if (includeSimilar && testQuestions.length < questionCount) {
      const remainingCount = questionCount - testQuestions.length;
      const topics = [...new Set(testQuestions.map(q => q.topic || q.subject))];
      
      for (const topic of topics) {
        if (testQuestions.length >= questionCount) break;
        
        const similarQuestionsResult = await getQuestionsFromBank({
          topic: topic,
          limit: Math.ceil(remainingCount / topics.length),
          difficulty: difficulty
        });

        if (similarQuestionsResult.success) {
          const newQuestions = similarQuestionsResult.data.filter(q => 
            !testQuestions.some(existing => existing.id === q.id)
          );
          testQuestions.push(...newQuestions);
        }
      }
    }

    // Shuffle questions
    testQuestions = testQuestions.sort(() => Math.random() - 0.5);

    return {
      success: true,
      data: {
        questions: testQuestions,
        totalQuestions: testQuestions.length,
        duration: testQuestions.length * 3 * 60, // 3 minutes per question
        type: 'revision',
        topic: topic,
        difficulty: difficulty
      }
    };
  } catch (error) {
    // Error generating revision test
    return { success: false, error: error.message };
  }
};

// Generate practice session
export const generatePracticeSession = async (userId, sessionConfig) => {
  try {
    const {
      topic,
      questionCount = 10,
      difficulty = 'mixed',
      mode = 'timed'
    } = sessionConfig;

    // Get questions from question bank
    const questionsResult = await getQuestionsFromBank({
      topic: topic,
      limit: questionCount,
      difficulty: difficulty
    });

    if (!questionsResult.success || questionsResult.data.length === 0) {
      throw new Error('No questions available for this topic');
    }

    const questions = questionsResult.data.sort(() => Math.random() - 0.5);

    return {
      success: true,
      data: {
        questions: questions,
        totalQuestions: questions.length,
        duration: mode === 'timed' ? questions.length * 2 * 60 : 0, // 2 minutes per question
        type: 'practice',
        topic: topic,
        difficulty: difficulty,
        mode: mode
      }
    };
  } catch (error) {
    // Error generating practice session
    return { success: false, error: error.message };
  }
};

// Save practice session results
export const savePracticeResults = async (userId, sessionData) => {
  try {
    const {
      topic,
      questions,
      correctAnswers,
      totalQuestions,
      duration,
      accuracy,
      mode
    } = sessionData;

    const sessionResult = await savePracticeSession(userId, {
      topic: topic,
      questionCount: totalQuestions,
      correctAnswers: correctAnswers,
      accuracy: accuracy,
      duration: duration,
      mode: mode,
      completedAt: new Date()
    });

    if (sessionResult.success) {
      // Update user's revision data
      await updateUserRevisionStats(userId, {
        topic: topic,
        accuracy: accuracy,
        questionsAnswered: totalQuestions,
        correctAnswers: correctAnswers
      });
    }

    return sessionResult;
  } catch (error) {
    // Error saving practice results
    return { success: false, error: error.message };
  }
};

// Update user revision statistics
export const updateUserRevisionStats = async (userId, stats) => {
  try {
    // Get current revision data
    const currentData = await getUserRevisionDataByUserId(userId);
    let revisionData = currentData.success ? currentData.data : {};

    // Initialize if empty
    if (!revisionData) {
      revisionData = {
        userId: userId,
        weakTopics: [],
        practiceSessions: [],
        totalQuestionsAnswered: 0,
        totalCorrectAnswers: 0,
        averageAccuracy: 0,
        lastUpdated: new Date()
      };
    }

    // Update topic performance
    const topicIndex = revisionData.weakTopics.findIndex(t => t.name === stats.topic);
    if (topicIndex >= 0) {
      const topic = revisionData.weakTopics[topicIndex];
      const totalQuestions = topic.totalQuestions + stats.questionsAnswered;
      const totalCorrect = topic.totalCorrect + stats.correctAnswers;
      
      revisionData.weakTopics[topicIndex] = {
        ...topic,
        totalQuestions: totalQuestions,
        totalCorrect: totalCorrect,
        accuracy: Math.round((totalCorrect / totalQuestions) * 100),
        lastPracticed: new Date()
      };
    } else {
      revisionData.weakTopics.push({
        name: stats.topic,
        totalQuestions: stats.questionsAnswered,
        totalCorrect: stats.correctAnswers,
        accuracy: Math.round((stats.correctAnswers / stats.questionsAnswered) * 100),
        lastPracticed: new Date()
      });
    }

    // Update overall stats
    revisionData.totalQuestionsAnswered += stats.questionsAnswered;
    revisionData.totalCorrectAnswers += stats.correctAnswers;
    revisionData.averageAccuracy = Math.round((revisionData.totalCorrectAnswers / revisionData.totalQuestionsAnswered) * 100);
    revisionData.lastUpdated = new Date();

    // Save updated data
    const result = await saveUserRevisionData(userId, revisionData);
    return result;
  } catch (error) {
    // Error updating revision stats
    return { success: false, error: error.message };
  }
};

// Analyze user performance and generate recommendations
export const generateRevisionInsights = async (userId) => {
  try {
    const revisionData = await getUserRevisionDataByUserId(userId);
    if (!revisionData.success || !revisionData.data) {
      return { success: false, error: 'No revision data found' };
    }

    const data = revisionData.data;
    const insights = {
      weakTopics: data.weakTopics
        .filter(topic => topic.accuracy < 70)
        .sort((a, b) => a.accuracy - b.accuracy)
        .slice(0, 5),
      
      strongTopics: data.weakTopics
        .filter(topic => topic.accuracy >= 80)
        .sort((a, b) => b.accuracy - a.accuracy)
        .slice(0, 3),
      
      improvementAreas: data.weakTopics
        .filter(topic => topic.accuracy >= 50 && topic.accuracy < 70)
        .sort((a, b) => a.accuracy - b.accuracy)
        .slice(0, 3),
      
      recommendations: [],
      overallProgress: {
        totalQuestions: data.totalQuestionsAnswered || 0,
        averageAccuracy: data.averageAccuracy || 0,
        totalSessions: data.practiceSessions?.length || 0
      }
    };

    // Generate recommendations
    if (insights.weakTopics.length > 0) {
      insights.recommendations.push({
        type: 'focus_weak_topic',
        priority: 'high',
        message: `Focus on ${insights.weakTopics[0].name} - your accuracy is ${insights.weakTopics[0].accuracy}%`,
        action: `Start practice session for ${insights.weakTopics[0].name}`
      });
    }

    if (data.totalQuestionsAnswered < 50) {
      insights.recommendations.push({
        type: 'more_practice',
        priority: 'medium',
        message: 'You need more practice. Aim for at least 50 questions answered.',
        action: 'Start a general revision test'
      });
    }

    if (data.averageAccuracy < 60) {
      insights.recommendations.push({
        type: 'improve_accuracy',
        priority: 'high',
        message: 'Your overall accuracy is low. Focus on understanding concepts better.',
        action: 'Review wrong questions and explanations'
      });
    }

    return { success: true, data: insights };
  } catch (error) {
    // Error generating insights
    return { success: false, error: error.message };
  }
};

// Calculate study streak
export const calculateStudyStreak = (practiceSessions) => {
  if (!practiceSessions || practiceSessions.length === 0) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const sortedSessions = practiceSessions
    .map(session => new Date(session.createdAt || session.completedAt))
    .sort((a, b) => b - a);

  let streak = 0;
  let currentDate = today;

  for (const sessionDate of sortedSessions) {
    const sessionDay = new Date(sessionDate);
    sessionDay.setHours(0, 0, 0, 0);

    const diffDays = Math.floor((currentDate - sessionDay) / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) {
      streak++;
      currentDate = sessionDay;
    } else {
      break;
    }
  }

  return streak;
};

// Format time duration
export const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

// Get difficulty color
export const getDifficultyColor = (difficulty) => {
  switch (difficulty?.toLowerCase()) {
    case 'easy':
      return 'text-green-600 bg-green-100';
    case 'medium':
      return 'text-yellow-600 bg-yellow-100';
    case 'hard':
      return 'text-red-600 bg-red-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

// Get accuracy color
export const getAccuracyColor = (accuracy) => {
  if (accuracy >= 90) return 'text-green-600 bg-green-100';
  if (accuracy >= 80) return 'text-blue-600 bg-blue-100';
  if (accuracy >= 70) return 'text-yellow-600 bg-yellow-100';
  return 'text-red-600 bg-red-100';
};
